import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import type { BlogPostDto, EditorJsContent } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import { CountriesService } from '../../../shared/services/countries.service';
import { MediaService } from '../../../shared/services/media.service';
import { AdminButtonComponent, AdminConfirmDialogService, AdminModalComponent, AdminToastService } from '../../../shared/admin';
import { PermissionService } from '../../permissions/permission.service';

type EditorJsInstance = {
  save: () => Promise<EditorJsContent>;
  destroy: () => void;
  isReady: Promise<void>;
  blocks: {
    insert: (
      type: string,
      data?: Record<string, unknown>,
      config?: unknown,
      index?: number,
      needToFocus?: boolean,
    ) => Promise<void>;
    getCurrentBlockIndex: () => number;
    convert: (index: number, type: string, data?: Record<string, unknown>) => Promise<void>;
    getBlockByIndex: (index: number) => { name: string } | undefined;
  };
  caret?: {
    setToBlock: (index: number, position?: 'start' | 'end') => void;
    focus: (atEnd?: boolean) => void;
  };
};

type EditorSection = 'content' | 'metadata' | 'assets';

@Component({
  selector: 'app-platform-blog-editor-page',
  standalone: true,
  imports: [FormsModule, RouterLink, AdminModalComponent, AdminButtonComponent],
  templateUrl: './blog-editor-page.component.html',
  host: { class: 'cg-blog-editor-page-host' },
})
export class PlatformBlogEditorPageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('editorHolder') editorHolder?: ElementRef<HTMLDivElement>;
  @ViewChild('editorBody') editorBody?: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarImageInput') toolbarImageInput?: ElementRef<HTMLInputElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly blogs = inject(BlogsService);
  private readonly media = inject(MediaService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);
  private readonly countries = inject(CountriesService);
  private readonly permissions = inject(PermissionService);

  readonly showEdit = computed(() => this.permissions.canShow('website.blogs', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.blogs', 'edit'));
  readonly showCreate = computed(() => this.permissions.canShow('website.blogs', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.blogs', 'create'));
  readonly showPublish = computed(() => this.permissions.canShow('website.blogs', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('website.blogs', 'publish'));
  readonly showDuplicate = computed(() => this.permissions.canShow('website.blogs', 'duplicate'));
  readonly canDuplicate = computed(() => this.permissions.canExecute('website.blogs', 'duplicate'));
  readonly showSchedule = computed(() => this.permissions.canShow('website.blogs', 'schedule'));
  readonly canSchedule = computed(() => this.permissions.canExecute('website.blogs', 'schedule'));
  readonly showRestoreVersion = computed(() =>
    this.permissions.canShow('website.blogs', 'restoreVersion'),
  );
  readonly canRestoreVersion = computed(() =>
    this.permissions.canExecute('website.blogs', 'restoreVersion'),
  );
  readonly showSaveDraft = computed(() =>
    this.isNewPost() ? this.showCreate() : this.showEdit(),
  );
  readonly canSaveDraft = computed(() =>
    this.isNewPost() ? this.canCreate() : this.canEdit(),
  );

  readonly postId = signal<string | null>(null);
  readonly isNewPost = signal(false);
  readonly post = signal<BlogPostDto | null>(null);
  readonly countryOptions = signal<Array<{ _id: string; name: string }>>([]);
  readonly validationErrors = signal<string[]>([]);
  readonly autosaveLabel = signal('');
  readonly saving = signal(false);
  readonly loading = signal(true);
  readonly versions = signal<Array<{ _id: string; versionNumber: number; label?: string; createdAt: string }>>([]);
  readonly activeSection = signal<EditorSection>('content');
  readonly versionsOpen = signal(false);
  readonly settingsOpen = signal(false);
  readonly linkModalOpen = signal(false);
  readonly featuredImagePreviewUrl = signal('');
  readonly guideFileName = signal('');

  readonly navItems: Array<{ id: EditorSection; label: string; icon: string }> = [
    { id: 'content', label: 'Content', icon: '☰' },
    { id: 'metadata', label: 'Metadata', icon: '▤' },
    { id: 'assets', label: 'Assets', icon: '▣' },
  ];

  linkDraft = '';

  title = '';
  slug = '';
  excerpt = '';
  status: BlogPostDto['status'] = 'DRAFT';
  scheduledAt = '';
  countryIds: string[] = [];
  tagsInput = '';
  isFeatured = false;
  featuredImageMediaAssetId = '';
  downloadableGuideMediaAssetId = '';
  faqItems: Array<{ question: string; answer: string }> = [];

  private editor?: EditorJsInstance;
  private pendingEditorContent?: EditorJsContent;
  private viewReady = false;
  private editorInitGeneration = 0;
  private ensuringTrailingParagraph = false;
  private trailingEnsureTimer?: ReturnType<typeof setTimeout>;
  private editorContinuityAbort?: AbortController;
  private readonly autosave$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.countries.listPlatformCountries().subscribe({
      next: (list) => this.countryOptions.set(list.map((c) => ({ _id: c._id, name: c.name }))),
    });

    this.autosave$
      .pipe(debounceTime(3000), takeUntil(this.destroy$))
      .subscribe(() => this.persist(true));

    const id = this.route.snapshot.paramMap.get('id');
    const isNew = this.route.snapshot.url.some((segment) => segment.path === 'new');

    if (isNew) {
      this.isNewPost.set(true);
      this.loading.set(false);
      return;
    }

    if (!id) {
      this.loading.set(false);
      void this.router.navigate(['/platform/blogs']);
      return;
    }
    this.postId.set(id);
    this.blogs.getPlatformById(id).subscribe({
      next: (p) => this.applyPost(p),
      error: () => this.loading.set(false),
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scheduleEditorInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.editorContinuityAbort?.abort();
    if (this.trailingEnsureTimer) clearTimeout(this.trailingEnsureTimer);
    this.editor?.destroy();
  }

  onFieldChange(): void {
    if (!this.isNewPost()) {
      this.autosave$.next();
    }
  }

  requiresSavedPost(action: string): boolean {
    if (!this.isNewPost()) return false;
    this.toast.error(`Save draft first to ${action}.`);
    return true;
  }

  displayTitle(): string {
    return this.title.trim() || 'Untitled Article';
  }

  statusLabel(): string {
    if (this.status === 'SCHEDULED') return 'Scheduled';
    if (this.status === 'PUBLISHED') return 'Published';
    return 'Draft';
  }

  setSection(section: EditorSection): void {
    this.activeSection.set(section);
    const targetId =
      section === 'content' || section === 'metadata'
        ? 'blog-editor-section-content'
        : 'blog-editor-section-assets';
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  saveDraft(): void {
    void this.persist(false);
  }

  preventToolbarFocusLoss(event: MouseEvent): void {
    event.preventDefault();
  }

  onEditorBodyClick(event: MouseEvent): void {
    const holder = this.editorHolder?.nativeElement;
    if (!holder) return;

    const target = event.target as HTMLElement;
    if (target.closest('[contenteditable="true"]')) return;

    const editable =
      holder.querySelector<HTMLElement>('[contenteditable="true"]') ??
      holder.querySelector<HTMLElement>('.ce-paragraph') ??
      holder.querySelector<HTMLElement>('.ce-block');

    editable?.focus();

    if (editable?.isContentEditable && document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(event.clientX, event.clientY);
      if (range && holder.contains(range.startContainer)) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }

  formatInline(command: 'bold' | 'italic' | 'underline'): void {
    if (!this.focusEditorSelection()) return;
    document.execCommand(command, false);
    this.notifyEditorChange();
  }

  formatLink(): void {
    if (!this.focusEditorSelection()) return;
    this.linkDraft = '';
    this.linkModalOpen.set(true);
  }

  closeLinkModal(): void {
    this.linkModalOpen.set(false);
    this.linkDraft = '';
  }

  applyLinkFromModal(): void {
    const url = this.linkDraft.trim();
    if (!url) return;
    if (!this.focusEditorSelection()) return;

    const selection = window.getSelection();
    if (selection?.isCollapsed) {
      document.execCommand(
        'insertHTML',
        false,
        `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(url)}</a>`,
      );
    } else {
      document.execCommand('createLink', false, url);
    }

    this.notifyEditorChange();
    this.closeLinkModal();
  }

  formatCode(): void {
    if (!this.focusEditorSelection()) return;

    const selection = window.getSelection();
    const selectedText = selection?.toString() ?? '';
    document.execCommand('insertHTML', false, `<code>${this.escapeHtml(selectedText || 'code')}</code>`);
    this.notifyEditorChange();
  }

  insertBulletList(): void {
    void this.insertEditorBlock('list', { style: 'unordered', items: [''] });
  }

  insertNumberedList(): void {
    void this.insertEditorBlock('list', { style: 'ordered', items: [''] });
  }

  insertQuote(): void {
    void this.insertEditorBlock('quote', { text: '', caption: '' });
  }

  insertHeading(): void {
    void this.withEditor(async (editor) => {
      const index = editor.blocks.getCurrentBlockIndex();
      if (index < 0) {
        await editor.blocks.insert('header', { text: '', level: 2 });
        this.notifyEditorChange();
        await this.ensureTrailingParagraph(true);
        return;
      }

      const block = editor.blocks.getBlockByIndex(index);
      if (block?.name === 'header') {
        await editor.blocks.convert(index, 'paragraph', { text: '' });
      } else {
        await editor.blocks.convert(index, 'header', { text: '', level: 2 });
      }
      this.notifyEditorChange();
      await this.ensureTrailingParagraph(true);
    });
  }

  openToolbarImagePicker(): void {
    this.toolbarImageInput?.nativeElement.click();
  }

  onToolbarImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) void this.uploadToolbarImage(file);
  }

  private async uploadToolbarImage(file: File): Promise<void> {
    const alt = this.imageAltFromFile(file);
    this.media.upload(file, { altText: alt }).subscribe({
      next: (asset) => {
        void this.insertEditorBlock('image', {
          file: { url: asset.url, mediaAssetId: asset._id },
          caption: alt,
          withBorder: false,
          withBackground: false,
          stretched: false,
        });
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not upload image.'),
    });
  }

  private async insertEditorBlock(type: string, data: Record<string, unknown>): Promise<void> {
    await this.withEditor(async (editor) => {
      await editor.blocks.insert(type, data);
      this.notifyEditorChange();
      await this.ensureTrailingParagraph(true);
    });
  }

  private async withEditor(run: (editor: EditorJsInstance) => Promise<void> | void): Promise<void> {
    const editor = this.editor;
    if (!editor) return;
    await editor.isReady;
    await run(editor);
  }

  private focusEditorSelection(): boolean {
    const selection = window.getSelection();
    if (!selection?.anchorNode) {
      const editable = this.editorHolder?.nativeElement.querySelector<HTMLElement>('[contenteditable="true"]');
      editable?.focus();
      return !!editable;
    }

    let node: Node | null = selection.anchorNode;
    while (node) {
      if (node instanceof HTMLElement && node.isContentEditable) {
        node.focus();
        return true;
      }
      node = node.parentNode;
    }

    return false;
  }

  private notifyEditorChange(): void {
    if (!this.isNewPost()) {
      this.autosave$.next();
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private imageAltFromFile(file: File): string {
    const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
    return name || 'Image';
  }

  toggleVersions(): void {
    if (this.requiresSavedPost('view version history')) return;
    const next = !this.versionsOpen();
    this.versionsOpen.set(next);
    this.settingsOpen.set(false);
    if (next) this.loadVersions();
  }

  toggleSettings(): void {
    this.settingsOpen.update((v) => !v);
    this.versionsOpen.set(false);
  }

  onFeaturedDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onFeaturedDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) this.uploadFeaturedFile(file);
  }

  removeGuide(): void {
    this.downloadableGuideMediaAssetId = '';
    this.guideFileName.set('');
    this.onFieldChange();
  }

  toggleCountry(id: string, checked: boolean): void {
    if (checked) this.countryIds = [...new Set([...this.countryIds, id])];
    else this.countryIds = this.countryIds.filter((c) => c !== id);
    this.onFieldChange();
  }

  addFaq(): void {
    this.faqItems = [...this.faqItems, { question: '', answer: '' }];
  }

  removeFaq(index: number): void {
    this.faqItems = this.faqItems.filter((_, i) => i !== index);
    this.onFieldChange();
  }

  async uploadFeatured(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.uploadFeaturedFile(file);
  }

  private uploadFeaturedFile(file: File): void {
    const alt = this.imageAltFromFile(file);
    this.media.upload(file, { altText: alt }).subscribe({
      next: (asset) => {
        this.featuredImageMediaAssetId = asset._id;
        this.featuredImagePreviewUrl.set(asset.url);
        this.onFieldChange();
      },
    });
  }

  async uploadGuide(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.media.upload(file, { altText: file.name }).subscribe({
      next: (asset) => {
        this.downloadableGuideMediaAssetId = asset._id;
        this.guideFileName.set(asset.originalName || file.name);
        this.onFieldChange();
      },
    });
  }

  validate(): void {
    if (this.requiresSavedPost('validate')) return;
    const id = this.postId();
    if (!id) return;
    this.blogs.validatePublish(id).subscribe({
      next: (r) => this.validationErrors.set(r.errors),
    });
  }

  publish(): void {
    void this.persist(false).then(() => {
      const id = this.postId();
      if (!id) return;
      this.blogs.publish(id).subscribe({
        next: (p) => {
          this.applyPost(p);
          this.validationErrors.set([]);
          this.toast.success('Blog post published.');
        },
        error: (err) => {
          const errors = err?.error?.errors as string[] | undefined;
          this.validationErrors.set(errors ?? [err?.error?.message ?? 'Publish failed']);
          this.toast.error(err?.error?.message ?? 'Publish failed. Fix validation issues.');
        },
      });
    });
  }

  schedule(): void {
    if (!this.scheduledAt) return;
    void this.persist(false).then(() => {
      const id = this.postId();
      if (!id) return;
      this.blogs.schedule(id, new Date(this.scheduledAt).toISOString()).subscribe({
        next: (p) => this.applyPost(p),
        error: (err) => this.validationErrors.set(err?.error?.errors ?? [err?.error?.message ?? 'Schedule failed']),
      });
    });
  }

  unpublish(): void {
    const id = this.postId();
    if (!id) return;
    this.blogs.unpublish(id).subscribe({ next: (p) => this.applyPost(p) });
  }

  preview(): void {
    void this.persist(false).then(() => {
      const id = this.postId();
      if (!id) return;
      this.blogs.previewToken(id).subscribe({
        next: ({ token }) => window.open(`/blog/preview/${token}`, '_blank'),
      });
    });
  }

  duplicate(): void {
    if (this.requiresSavedPost('duplicate')) return;
    const id = this.postId();
    if (!id) return;
    this.blogs.duplicate(id).subscribe({
      next: (copy) => this.router.navigate(['/platform/blogs', copy._id, 'edit']),
    });
  }

  loadVersions(): void {
    const id = this.postId();
    if (!id) return;
    this.blogs.listVersions(id).subscribe((v) => this.versions.set(v));
  }

  restoreVersion(versionId: string): void {
    void this.confirmRestoreVersion(versionId);
  }

  private async confirmRestoreVersion(versionId: string): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Restore version',
      message: 'Restore this version? Your current content will be snapshotted first.',
      confirmLabel: 'Restore',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    const id = this.postId();
    if (!id) return;

    this.blogs.restoreVersion(id, versionId).subscribe({
      next: (p) => {
        this.applyPost(p);
        this.toast.success('Version restored.');
        this.versionsOpen.set(false);
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not restore version.'),
    });
  }

  private applyPost(p: BlogPostDto): void {
    this.post.set(p);
    this.title = p.title;
    this.slug = p.slug ?? '';
    this.excerpt = p.excerpt;
    this.status = p.status;
    this.scheduledAt = p.scheduledAt ? p.scheduledAt.slice(0, 16) : '';
    this.countryIds = [...(p.countryIds ?? [])];
    this.tagsInput = (p.tags ?? []).join(', ');
    this.isFeatured = p.isFeatured;
    this.featuredImageMediaAssetId = p.featuredImageMediaAssetId ?? '';
    this.downloadableGuideMediaAssetId = p.downloadableGuideMediaAssetId ?? '';
    this.faqItems = [...(p.faqItems ?? [])];
    this.loading.set(false);
    if (p.featuredImageMediaAssetId) {
      this.media.getById(p.featuredImageMediaAssetId).subscribe({
        next: (asset) => this.featuredImagePreviewUrl.set(asset.url),
        error: () => this.featuredImagePreviewUrl.set(''),
      });
    } else {
      this.featuredImagePreviewUrl.set('');
    }
    if (p.downloadableGuideMediaAssetId) {
      this.media.getById(p.downloadableGuideMediaAssetId).subscribe({
        next: (asset) => this.guideFileName.set(asset.originalName),
        error: () => this.guideFileName.set(''),
      });
    } else {
      this.guideFileName.set('');
    }
    this.scheduleEditorInit(p.content);
  }

  private scheduleEditorInit(data?: EditorJsContent): void {
    if (data !== undefined) {
      this.pendingEditorContent = data;
    }
    setTimeout(() => void this.tryInitEditor(), 0);
  }

  private async tryInitEditor(): Promise<void> {
    if (!this.viewReady || this.loading()) return;
    if (!this.editorHolder?.nativeElement) return;
    if (!this.isNewPost() && !this.post()) return;

    await this.initEditor(this.pendingEditorContent);
  }

  private async initEditor(data?: EditorJsContent): Promise<void> {
    if (!this.editorHolder?.nativeElement) return;

    const generation = ++this.editorInitGeneration;
    this.editorContinuityAbort?.abort();
    this.editor?.destroy();

    const [
      EditorJS,
      Header,
      Paragraph,
      List,
      ImageTool,
      Quote,
      Delimiter,
      Table,
      Warning,
      Checklist,
      Embed,
    ] = await Promise.all([
      import('@editorjs/editorjs').then((m) => m.default),
      import('@editorjs/header').then((m) => m.default),
      import('@editorjs/paragraph').then((m) => m.default),
      import('@editorjs/list').then((m) => m.default),
      import('@editorjs/image').then((m) => m.default),
      import('@editorjs/quote').then((m) => m.default),
      import('@editorjs/delimiter').then((m) => m.default),
      import('@editorjs/table').then((m) => m.default),
      import('@editorjs/warning').then((m) => m.default),
      import('@editorjs/checklist').then((m) => m.default),
      import('@editorjs/embed').then((m) => m.default),
    ]);

    const mediaService = this.media;
    const altFromFile = (file: File) => this.imageAltFromFile(file);

    const editorData = {
      time: data?.time ?? Date.now(),
      version: data?.version ?? '2.28.0',
      blocks: data?.blocks ?? [],
    };

    this.editor = new EditorJS({
      holder: this.editorHolder.nativeElement,
      data: editorData,
      minHeight: 400,
      autofocus: false,
      placeholder: 'Start typing your content here...',
      onChange: () => {
        this.scheduleEnsureTrailingParagraph();
        if (!this.isNewPost()) {
          this.autosave$.next();
        }
      },
      tools: {
        header: { class: Header, inlineToolbar: true },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true,
          config: {
            placeholder: 'Start typing here...',
            preserveBlank: true,
          },
        },
        list: { class: List, inlineToolbar: true },
        quote: { class: Quote, inlineToolbar: true },
        delimiter: Delimiter,
        table: Table,
        warning: Warning,
        checklist: { class: Checklist, inlineToolbar: true },
        embed: Embed,
        image: {
          class: ImageTool,
          config: {
            uploader: {
              async uploadByFile(file: File) {
                const alt = altFromFile(file);
                const asset = await new Promise<{ url: string; mediaAssetId: string }>((resolve, reject) => {
                  mediaService.upload(file, { altText: alt }).subscribe({
                    next: (a) => resolve({ url: a.url, mediaAssetId: a._id }),
                    error: reject,
                  });
                });
                return {
                  success: 1,
                  file: { url: asset.url, mediaAssetId: asset.mediaAssetId },
                };
              },
            },
          },
        },
      },
    } as ConstructorParameters<typeof EditorJS>[0]) as unknown as EditorJsInstance;

    await this.editor.isReady;
    if (generation !== this.editorInitGeneration) return;
    await this.ensureTrailingParagraph();
    this.bindEditorContinuityHandlers();
  }

  private scheduleEnsureTrailingParagraph(): void {
    if (this.trailingEnsureTimer) clearTimeout(this.trailingEnsureTimer);
    this.trailingEnsureTimer = setTimeout(() => void this.ensureTrailingParagraph(), 80);
  }

  private bindEditorContinuityHandlers(): void {
    const holder = this.editorHolder?.nativeElement;
    if (!holder) return;

    this.editorContinuityAbort?.abort();
    const controller = new AbortController();
    this.editorContinuityAbort = controller;
    const { signal } = controller;

    holder.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        setTimeout(() => void this.ensureTrailingParagraph(true), 0);
      },
      { signal },
    );

    holder.addEventListener(
      'keyup',
      () => this.scheduleEnsureTrailingParagraph(),
      { signal },
    );
  }

  private async ensureTrailingParagraph(focusNew = false): Promise<void> {
    const editor = this.editor;
    const holder = this.editorHolder?.nativeElement;
    if (!editor || !holder || this.ensuringTrailingParagraph) return;

    await editor.isReady;

    const blockCount = holder.querySelectorAll('.ce-block').length;

    if (blockCount === 0) {
      this.ensuringTrailingParagraph = true;
      try {
        await editor.blocks.insert('paragraph', { text: '' }, {}, undefined, focusNew);
      } finally {
        this.ensuringTrailingParagraph = false;
      }
      return;
    }

    if (!this.lastBlockIsEmptyParagraph()) {
      this.ensuringTrailingParagraph = true;
      try {
        await editor.blocks.insert('paragraph', { text: '' }, {}, blockCount, focusNew);
      } finally {
        this.ensuringTrailingParagraph = false;
      }
      return;
    }

    if (focusNew) {
      this.focusLastParagraph();
    }
  }

  private lastBlockIsEmptyParagraph(): boolean {
    const holder = this.editorHolder?.nativeElement;
    if (!holder) return false;

    const blocks = holder.querySelectorAll('.ce-block');
    const lastBlock = blocks[blocks.length - 1];
    if (!lastBlock) return false;

    const paragraph = lastBlock.querySelector('.ce-paragraph');
    if (!paragraph) return false;

    return this.isEmptyParagraphText(paragraph.textContent ?? '');
  }

  private focusLastParagraph(): void {
    const holder = this.editorHolder?.nativeElement;
    if (!holder) return;

    const paragraphs = holder.querySelectorAll<HTMLElement>('.ce-paragraph[contenteditable="true"]');
    const last = paragraphs[paragraphs.length - 1];
    if (!last) return;

    last.focus();
    const editor = this.editor;
    const index = holder.querySelectorAll('.ce-block').length - 1;
    if (editor?.caret && index >= 0) {
      editor.caret.setToBlock(index, 'end');
    }
  }

  private isEmptyParagraphText(text: string): boolean {
    return text.replace(/\u200B/g, '').trim() === '';
  }

  private isEmptyParagraphData(data: Record<string, unknown>): boolean {
    const html = String(data['text'] ?? '');
    const stripped = html
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, '')
      .replace(/<[^>]+>/g, '')
      .trim();
    return stripped === '';
  }

  private stripTrailingEmptyParagraphs(
    blocks: NonNullable<EditorJsContent['blocks']>,
  ): NonNullable<EditorJsContent['blocks']> {
    const result = [...blocks];
    while (result.length > 0) {
      const last = result[result.length - 1];
      if (last.type === 'paragraph' && this.isEmptyParagraphData(last.data)) {
        result.pop();
      } else {
        break;
      }
    }
    return result;
  }

  private async persist(autosave: boolean): Promise<void> {
    if (!this.editor) return;
    if (this.isNewPost() && autosave) return;

    this.saving.set(true);
    const saved = await this.editor.save();
    const content: EditorJsContent = {
      ...saved,
      blocks: this.stripTrailingEmptyParagraphs(saved.blocks ?? []),
    };
    const payload = {
      title: this.title,
      slug: this.slug || undefined,
      excerpt: this.excerpt,
      content,
      countryIds: this.countryIds,
      tags: this.tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
      isFeatured: this.isFeatured,
      featuredImageMediaAssetId: this.featuredImageMediaAssetId || undefined,
      downloadableGuideMediaAssetId: this.downloadableGuideMediaAssetId || undefined,
      faqItems: this.faqItems.filter((f) => f.question && f.answer),
    };

    if (this.isNewPost()) {
      return new Promise((resolve) => {
        this.blogs.createPlatform(payload).subscribe({
          next: (p) => {
            this.isNewPost.set(false);
            this.postId.set(p._id);
            this.post.set(p);
            this.slug = p.slug ?? this.slug;
            this.autosaveLabel.set(`Saved ${new Date().toLocaleTimeString()}`);
            this.saving.set(false);
            this.toast.success('Blog post created.');
            void this.router.navigate(['/platform/blogs', p._id, 'edit'], { replaceUrl: true });
            resolve();
          },
          error: (err) => {
            this.toast.error(err?.error?.message ?? 'Could not save blog post.');
            this.saving.set(false);
            resolve();
          },
        });
      });
    }

    const id = this.postId();
    if (!id) {
      this.saving.set(false);
      return;
    }

    return new Promise((resolve) => {
      this.blogs.updatePlatform(id, payload, autosave).subscribe({
        next: (p) => {
          this.post.set(p);
          this.slug = p.slug ?? this.slug;
          this.autosaveLabel.set(`Saved ${new Date().toLocaleTimeString()}`);
          this.saving.set(false);
          if (!autosave) {
            this.toast.success('Draft saved.');
          }
          resolve();
        },
        error: (err) => {
          this.toast.error(err?.error?.message ?? 'Could not save blog post.');
          this.saving.set(false);
          resolve();
        },
      });
    });
  }
}
